import { useState, useEffect, useRef } from 'react';
import _findIndex from 'lodash.findindex';
import _find from 'lodash.find';
import { DateTime } from 'luxon';
import { useQuery } from 'react-query';
import { Dropdown } from 'primereact/dropdown';
import { Messages } from 'primereact/messages';
import { Calendar } from 'primereact/calendar';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { getSourceDetailValues, getIsConfirmation } from '../../services/measurement';
import {
  ActionsPanel,
  BtnsWrapper,
  EditableValue,
  FilterWrapper,
  Filters,
  StaticValue,
  TableWrapper,
  ValueWrapper,
  getReasonStrById,
} from './common';
import EditCellModal from './editCellModal';
import { useReportDetailStore } from '.';
import { getFormattedNumberStr } from '../../utils/number';
import UnsavedChangesNote from './unsavedChangesNote';
import DiscardChangesModal from './discardChangesModal';
import MillSourcesSummary from './millSourcesSummary';
import ConfirmAllValuesModal from './confirmAllValuesModal';
import { useConfirmAllEntries, useSubmitSources } from '../../services/mutations';
import ExportToExcelButton from '../Excel/exportToExcelButton';
import { TabName } from '../Excel/constants';

function SourcesView({
  placeCode,
  sourcesData,
  reasonsData,
  tabName,
}: {
  placeCode: string;
  sourcesData: any[];
  reasonsData: any[];
  tabName: TabName;
}) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);
  const [gridData, setGridData] = useState([]);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState<boolean>(false);
  const [selectedYearMonth, setSelectedYearMonth] = useState(DateTime.now().minus({ months: 1 }).toJSDate());
  const messages = useRef<Messages>(null);
  const submitChanges = useSubmitSources();
  const [showConfirmValuesModal, setShowConfirmValuesModal] = useState<boolean>(false);
  const confirmEntries = useConfirmAllEntries();
  const { updatedCells, resultToast, setUpdatedCells, hasUnsavedData, userPermissions } = useReportDetailStore();

  const getYearMonthNumber = (value: Date) =>
    Number(value.getFullYear().toString() + String(value.getMonth() + 1).padStart(2, '0'));

  const [isConfirmed, setIsConfirmed] = useState(false);

  const {
    data: loadedData,
    isFetching,
    error,
    refetch,
  } = useQuery(
    ['sourcesView', placeCode],
    () => getSourceDetailValues(placeCode, selectedSource || 0, getYearMonthNumber(selectedYearMonth)),
    { enabled: !!selectedSource },
  );

  const { data: isConfirmationData, refetch: isConfirmationRefetch } = useQuery(
    ['confirmationView', placeCode],
    () => getIsConfirmation(placeCode, Number(selectedSource), getYearMonthNumber(selectedYearMonth)),
    { enabled: !!selectedSource },
  );

  const valueCellTemplate = (rowData: any) => {
    const updatedValue = _find(updatedCells, (item: any) => item.metricId === rowData.data.metricId);
    const item = updatedValue ? { ...rowData.data, ...updatedValue } : rowData.data;

    return (
      <ValueWrapper>
        {userPermissions.canUpdateSources ? (
          <EditableValue
            onClick={() => setSelectedItem({ data: item, isUpdated: !!updatedValue })}
            isUpdated={!!updatedValue}
          >
            <i className="pi pi-pencil" />
            {getFormattedNumberStr(Math.round(item.value), item.metricUnit)}
          </EditableValue>
        ) : (
          <StaticValue>{getFormattedNumberStr(Math.round(item.value), item.metricUnit)}</StaticValue>
        )}
      </ValueWrapper>
    );
  };

  const dateTimeCellTemplate = (rowData: any) =>
    rowData.data.createdAt
      ? DateTime.fromISO(rowData.data.createdAt).toLocaleString({
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
        })
      : '-';

  const handleUpdatedCell = (values: any) => {
    const selectedMetricId = selectedItem?.data.metricId;

    const objIdx = _findIndex(updatedCells, (item: any) => item.metricId === selectedMetricId);

    if (objIdx !== -1) {
      const tmpUpdatedCells: any[] = updatedCells;
      const cell: any = updatedCells[objIdx];

      tmpUpdatedCells[objIdx] = {
        ...cell,
        value: values.value,
        reason: values.reason,
      };
      setUpdatedCells(tmpUpdatedCells);
    } else {
      setUpdatedCells([...updatedCells, { metricId: selectedMetricId, ...values }]);
    }

    setSelectedItem(null);
  };

  const handleDiscard = () => {
    setShowDiscardConfirmation(false);
    setUpdatedCells([]);
    resultToast.current?.show({
      severity: 'success',
      summary: 'Discard changes',
      detail: 'Changes has been successfully discarded.',
      life: 3000,
    });
  };

  const handleSubmitChanges = () => {
    submitChanges.mutate(
      { code: placeCode, yearMonth: getYearMonthNumber(selectedYearMonth), data: updatedCells },
      {
        onSuccess: () => {
          resultToast.current?.show({
            severity: 'success',
            summary: 'Submit changes',
            detail: 'Changes has been successfully submitted.',
            life: 3000,
          });
        },
        onError: () => {
          resultToast.current?.show({
            severity: 'error',
            summary: 'Submit changes',
            detail: 'An error has occurred while submitting changes.',
            life: 3000,
          });
        },
      },
    );
  };

  const handleConfirmValues = () => {
    setShowConfirmValuesModal(false);
    confirmEntries.mutate(
      { code: placeCode, yearMonth: getYearMonthNumber(selectedYearMonth), MetricTypeId: selectedSource },
      {
        onSuccess: () => {
          resultToast.current?.show({
            severity: 'success',
            summary: 'Confirm report entries',
            detail: 'All report entries has been successfully confirmed.',
            life: 3000,
          });
          setIsConfirmed(true);
        },
        onError: () => {
          resultToast.current?.show({
            severity: 'error',
            summary: 'Confirm report entries',
            detail: 'An error has occurred while confirming report entries.',
            life: 3000,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (error) {
      messages.current?.show({ severity: 'error', summary: 'An error has occurred while loading data!', sticky: true });
    }
  }, [error]);

  useEffect(() => {
    if (selectedSource) {
      refetch();
      isConfirmationRefetch();
      setIsConfirmed(false);
    }
  }, [selectedSource, selectedYearMonth, refetch]);

  useEffect(() => {
    if (sourcesData.length > 0) {
      setSelectedSource(sourcesData[0].id);
    }
  }, [sourcesData]);

  useEffect(() => {
    if (loadedData) {
      setUpdatedCells([]);

      const tempData = loadedData.items.map((item) => ({
        key: item.code,
        data: {
          label: item.name,
          level: 2,
          metricUnit: item.metricUnit,
          metricId: item.metricId,
          value: item.value,
          reasonId: item.reasonId,
          createdAt: item.createdAt,
          createdBy: item.createdBy,
        },
        children: [
          ...item.items.map((ltItem: any) => ({
            key: ltItem.code,
            data: {
              label: ltItem.name,
              level: 3,
              metricUnit: ltItem.metricUnit,
              metricId: ltItem.metricId,
              value: ltItem.value,
              reasonId: ltItem.reasonId,
              createdAt: ltItem.createdAt,
              createdBy: ltItem.createdBy,
            },
          })),
        ],
      }));

      setGridData(tempData);
    }
  }, [loadedData]);

  const submittingChanges = submitChanges.isLoading;

  const selectedCategoryName = selectedSource
    ? sourcesData.find((source) => source.id === selectedSource)?.name ?? 'Unknown'
    : 'Unknown';

  return (
    <>
      <Messages ref={messages} />

      <DiscardChangesModal
        isOpen={showDiscardConfirmation}
        onConfirm={handleDiscard}
        onCancel={() => setShowDiscardConfirmation(false)}
      />

      <ConfirmAllValuesModal
        isOpen={showConfirmValuesModal}
        onConfirm={handleConfirmValues}
        onCancel={() => setShowConfirmValuesModal(false)}
      />

      {selectedItem && (
        <EditCellModal
          item={selectedItem}
          defaultReason={3}
          reasonsData={reasonsData}
          onSubmit={handleUpdatedCell}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {loadedData && (
        <div>
          <ActionsPanel>
            <FilterWrapper>
              <Filters>
                <div>
                  <label htmlFor="source">Filter by type</label>
                  <Dropdown
                    style={{ width: '250px' }}
                    disabled={sourcesData.length === 0 || isFetching || hasUnsavedData() || submittingChanges}
                    value={selectedSource}
                    onChange={(e) => setSelectedSource(e.value)}
                    options={sourcesData}
                    optionValue="id"
                    optionLabel="name"
                  />
                </div>
                <div>
                  <label htmlFor="fromYearmonth">Filter by year month</label>
                  <Calendar
                    id="fromYearmonth"
                    view="month"
                    dateFormat="MM, yy"
                    readOnlyInput
                    keepInvalid
                    value={selectedYearMonth}
                    disabled={isFetching || hasUnsavedData() || submittingChanges}
                    onChange={(e) => {
                      setSelectedYearMonth(e.value);
                    }}
                    hideOnDateTimeSelect
                  />
                </div>
              </Filters>
              {hasUnsavedData() && (
                <UnsavedChangesNote onDiscard={() => setShowDiscardConfirmation(true)} onSubmit={handleSubmitChanges} />
              )}
            </FilterWrapper>
            <div>
              <BtnsWrapper>
                {userPermissions.canUpdateSources && (
                  <>
                    <Button
                      label="Discard changes"
                      size="small"
                      severity="warning"
                      disabled={!hasUnsavedData() || submittingChanges || isFetching}
                      onClick={() => setShowDiscardConfirmation(true)}
                    />
                    <Button
                      label="Save changes"
                      size="small"
                      onClick={handleSubmitChanges}
                      loading={submittingChanges}
                      disabled={!hasUnsavedData() || submittingChanges || isFetching}
                    />
                    <Button
                      label="Confirm values"
                      icon="pi pi-check-circle"
                      size="small"
                      onClick={() => setShowConfirmValuesModal(true)}
                      loading={submittingChanges}
                      disabled={submittingChanges || isFetching || isConfirmationData || isConfirmed}
                    />
                  </>
                )}
                <ExportToExcelButton
                  placeCode={placeCode}
                  tabName={tabName}
                  sourceName=''
                  selectedTime={getYearMonthNumber(selectedYearMonth)}
                  isDisabled={submittingChanges || isFetching}
                />
              </BtnsWrapper>
            </div>
          </ActionsPanel>

          <MillSourcesSummary
            data={loadedData}
            updatedCells={updatedCells}
            reasonsData={reasonsData}
            onUpdate={(item: any) => setSelectedItem(item)}
          />

          <TableWrapper>
            <TreeTable
              loading={isFetching}
              value={gridData}
              rowHover
              tableStyle={{ minWidth: '50rem' }}
              resizableColumns
              showGridlines
              columnResizeMode="expand"
            >
              <Column
                field="label"
                header="Plant Section"
                expander
                body={(rowData) => (rowData.data?.label ? `${rowData.data.label} (${rowData.key})` : '')}
                style={{ width: '450px' }}
              />
              <Column field="value" header="Value" sortable body={valueCellTemplate} />
              <Column
                field="reasonId"
                header="Reason"
                body={(rowData) => getReasonStrById(rowData.data?.reasonId, reasonsData)}
              />
              <Column field="createdAt" header="Created At" body={dateTimeCellTemplate} />
              <Column
                field="createdBy"
                header="Created By"
                body={(rowData) =>
                  rowData.data?.createdBy && rowData.data?.createdBy !== '' ? rowData.data?.createdBy : '-'
                }
              />
            </TreeTable>
          </TableWrapper>
          {/* <pre style={{ marginBottom: '16px' }}>{JSON.stringify(updatedCells, null, 2)}</pre> */}
        </div>
      )}
    </>
  );
}

export default SourcesView;
